<template>
  <span class="linkified-text">
    <template v-for="(seg, i) in segments" :key="i">
      <a
        v-if="seg.type === 'link'"
        :href="seg.href"
        target="_blank"
        rel="noopener noreferrer"
        class="linkified-link"
        >{{ seg.displayText
        }}<svg
          class="external-link-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <use href="@/assets/icons/iconset.svg#externalLink-tiny"></use></svg
      ></a>
      <br v-else-if="seg.type === 'newline'" />
      <span v-else>{{ seg.text }}</span>
    </template>
  </span>
</template>

<script>
export default {
  name: "LinkifiedText",
  props: {
    text: {
      type: String,
      default: "",
    },
    // If a URL starts with www., prepend https://
    autoHttpsForWww: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    segments() {
      const input = (this.text || "").toString();
      if (!input) return [];

      // Simple URL regex: http(s)://... or www.... Skip surrounding quotes/<>.
      const urlRegex = /((https?:\/\/)[^\s<>"]+|(?<!\w)(www\.[^\s<>"]+))/gi;
      const parts = [];
      let lastIndex = 0;
      let match;
      while ((match = urlRegex.exec(input)) !== null) {
        const start = match.index;
        const end = urlRegex.lastIndex;
        const before = input.slice(lastIndex, start);
        if (before) parts.push(...this.splitWithNewlines(before));

        let text = match[0];
        let href = text;
        if (!/^https?:\/\//i.test(href)) {
          if (this.autoHttpsForWww && /^www\./i.test(href)) {
            href = `https://${href}`;
          }
        }
        // Trim trailing punctuation common in prose
        const trimmed = this.trimTrailingPunctuation(text);
        if (trimmed !== text) {
          href = href.slice(0, href.length - (text.length - trimmed.length));
          text = trimmed;
        }
        // Create shortened display text (remove http(s)://www. and truncate to 10 chars)
        const displayText = this.shortenUrlForDisplay(text);
        parts.push({ type: "link", text, displayText, href });
        lastIndex = end;
      }
      const rest = input.slice(lastIndex);
      if (rest) parts.push(...this.splitWithNewlines(rest));
      return parts;
    },
  },
  methods: {
    shortenUrlForDisplay(url) {
      // Remove protocol (http:// or https://)
      let shortened = url.replace(/^https?:\/\//i, "");
      // Remove www.
      shortened = shortened.replace(/^www\./i, "");
      // Truncate to max 10 characters
      const maxLen = 10;
      if (shortened.length > maxLen) {
        shortened = shortened.substring(0, maxLen) + "…";
      }
      return shortened;
    },
    splitWithNewlines(chunk) {
      const out = [];
      const pieces = chunk.split(/(\n)/g);
      for (const p of pieces) {
        if (p === "\n") out.push({ type: "newline" });
        else if (p) out.push({ type: "text", text: p });
      }
      return out;
    },
    trimTrailingPunctuation(s) {
      // Remove trailing ), ], ., ,, !, ?, : from end if present (common around links)
      return s.replace(/[)\]\.,!?:]+$/g, "");
    },
  },
};
</script>

<style scoped>
.linkified-text {
  white-space: pre-line;
}

.linkified-link {
  color: var(--color-accent-primary);
  text-decoration: none;
  display: inline;
}

.linkified-link:hover {
  text-decoration: underline;
}

.external-link-icon {
  width: 0.85em;
  height: 0.85em;
  margin-left: 2px;
  vertical-align: baseline;
  display: inline;
  stroke: currentColor;
  stroke-width: 22;
  fill: none;
}
</style>
